/**
 * «Hacer un seguimiento» de una campaña que ya mandó (`campanas/:id/hacer-seguimiento`, 28/10/2026). Una página, no una
 * ventana encima: se elige a quiénes de los que recibieron la campaña se les escribe de nuevo, se ve cuántos quedan HOY
 * y se arma la campaña nueva (la «hija»), que se abre en el asistente de siempre.
 *
 * - Los criterios: «No la leyeron», «No tocaron el link» (sólo si la plantilla contaba los clics con el link propio) y
 *   «No compraron» en N días (contra la base de ventas en uso; los que no están en la base se suman sólo si se elige).
 *   El que no se puede elegir dice por qué, como lo manda el backend (sin base de ventas posterior, sin botón de link).
 * - Siempre afuera los que se dieron de baja, y los que respondieron salvo que se los sume. La cuenta de «Hoy quedan»
 *   ya pasa por las compuertas de cualquier campaña (la fatiga de OTRAS campañas incluida; la de ésta no cuenta).
 * - Es del equipo: la ruta va con `PaginaSoloEquipo` y el backend le niega todo a una persona de un cliente.
 * - Una campaña que ya tiene su seguimiento lleva a él (es uno por campaña).
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CornerDownRight, Loader2, Users } from "lucide-react";
import { oficialApi } from "../api";
import type { CampanaDetalle, CriterioSeguimiento, OpcionesDeSeguimiento, OpcionesSeguimiento } from "../types";
import { CRITERIOS_SEGUIMIENTO } from "../types";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { DETALLE_CRITERIO, TITULO_CRITERIO, desgloseSeguimiento, rutaAsistenteDeLaHija, rutaCampana } from "../logic/seguimiento";
import { Tarjeta } from "../components/Tarjeta";

const DIAS_POR_DEFECTO = 14;
const DIAS_MAX = 60;
const n = (x: number) => Number(x || 0).toLocaleString("es-AR");

/** Los días que se escribieron, si sirven (enteros de 1 a 60); si no, null. */
function diasValidos(texto: string): number | null {
  const d = Number(texto);
  return Number.isInteger(d) && d >= 1 && d <= DIAS_MAX ? d : null;
}

export default function CampanaSeguimientoPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [datos, setDatos] = useState<OpcionesDeSeguimiento | null>(null);
  const [error, setError] = useState("");
  const [noEncontrada, setNoEncontrada] = useState(false);
  const [cargando, setCargando] = useState(true);
  // null hasta la primera respuesta: sin criterio, el backend elige el primero que se puede
  const [criterio, setCriterio] = useState<CriterioSeguimiento | null>(null);
  const [diasTexto, setDiasTexto] = useState(String(DIAS_POR_DEFECTO));
  const [sinBase, setSinBase] = useState(false);
  const [respondieron, setRespondieron] = useState(false);
  const [armando, setArmando] = useState(false);
  const [errorArmar, setErrorArmar] = useState("");

  const dias = diasValidos(diasTexto);
  const diasMal = criterio === "no_compraron" && dias === null;

  const cargar = useCallback(async (o: Partial<OpcionesSeguimiento>) => {
    setCargando(true);
    try {
      const l = leerRespuesta<OpcionesDeSeguimiento>(await oficialApi.opcionesSeguimiento(id, o));
      if (!l.ok) { setError(textoError(l)); return; }
      setDatos(l.data);
      setError("");
      if (!o.criterio && l.data?.opciones?.criterio) setCriterio(l.data.opciones.criterio);
    } catch (e) {
      const x = leerError(e);
      if (x.status === 404) setNoEncontrada(true);
      else setError(textoError(x));
    } finally {
      setCargando(false);
    }
  }, [id]);

  // Cada cambio de opciones vuelve a contar (los días, sólo si sirven).
  useEffect(() => {
    if (criterio === "no_compraron" && dias === null) return;
    void cargar(criterio ? { criterio, dias: criterio === "no_compraron" ? dias : null, sinBase: criterio === "no_compraron" && sinBase, respondieron } : { respondieron });
  }, [cargar, criterio, dias, sinBase, respondieron]);

  const armar = async () => {
    if (!criterio || armando || diasMal) return;
    setArmando(true);
    setErrorArmar("");
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.crearSeguimiento(id, { criterio, dias: criterio === "no_compraron" ? dias : null, sinBase: criterio === "no_compraron" && sinBase, respondieron }));
      if (!l.ok || !l.data?.id) { setErrorArmar(l.ok ? "El servidor no devolvió el seguimiento." : textoError(l)); return; }
      navigate(rutaAsistenteDeLaHija(l.data.id));
    } catch (e) {
      // 409 SEGUIMIENTO_YA_EXISTE (otro lo armó mientras tanto): se recarga y la pantalla lleva al que ya está
      const x = leerError(e);
      setErrorArmar(textoError(x));
      if (x.code === "SEGUIMIENTO_YA_EXISTE") void cargar({ respondieron });
    } finally {
      setArmando(false);
    }
  };

  // en su propio renglón: al lado del título (inline) quedaba pegado al ícono («campaña⇄», prueba a mano del 28/10)
  const volver = (
    <div>
      <Link to={rutaCampana(id)} className="text-xs font-semibold text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
        <ArrowLeft size={13} /> Volver a la campaña
      </Link>
    </div>
  );

  if (noEncontrada) {
    return (
      <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center">
        <p className="text-sm text-slate-600">No encontramos esta campaña.</p>
        <Link to="/activa/oficial/campanas" className="mt-3 inline-block text-sm font-semibold text-violet-600 hover:underline">Volver a las campañas</Link>
      </div>
    );
  }
  if (!datos) {
    return (
      <div className="space-y-4">
        {volver}
        {error ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700" role="alert">
            <p className="font-semibold">No pudimos traer la campaña.</p>
            <p className="mt-1">{error}</p>
          </div>
        ) : (
          <div className="h-64 rounded-2xl bg-slate-100 animate-pulse" aria-busy="true" />
        )}
      </div>
    );
  }

  const c = datos.campana;
  const cabecera = (
    <div className="space-y-2">
      {volver}
      <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight inline-flex items-center gap-2">
        <CornerDownRight size={20} className="text-violet-600" aria-hidden="true" /> Hacer un seguimiento
      </h2>
      <p className="text-sm text-slate-500">
        De «{c.nombre}»: una campaña nueva sólo para una parte de los que la recibieron. Se arma como siempre (plantilla, variables, horario, prueba) y sale una sola vez.
      </p>
    </div>
  );

  // Ya tiene el suyo (el tope es uno por campaña): se lleva a él.
  if (datos.seguimiento) {
    return (
      <div className="space-y-5">
        {cabecera}
        <Tarjeta>
          <p className="text-sm text-slate-700">Esta campaña ya tiene su seguimiento: es uno por campaña.</p>
          <Link to={rutaCampana(datos.seguimiento.id)} className="mt-3 inline-flex h-9 px-3 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 items-center">
            Ver «{datos.seguimiento.nombre}»
          </Link>
        </Tarjeta>
      </div>
    );
  }
  if (datos.motivo) {
    return (
      <div className="space-y-5">
        {cabecera}
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="alert">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <p>{datos.motivo}</p>
        </div>
      </div>
    );
  }

  const el = datos.elegido;
  const desglose = el && criterio ? desgloseSeguimiento(el, criterio, sinBase) : [];
  const elegidoDisponible = !!criterio && !!datos.criterios?.[criterio]?.disponible;
  const motivoBoton = !criterio ? "Elegí a quiénes"
    : !elegidoDisponible ? "Ese criterio no se puede elegir"
    : diasMal ? `Los días van de 1 a ${DIAS_MAX}`
    : cargando ? "Contando…"
    : !el || el.pueden === 0 ? "Hoy no queda nadie a quien mandarle"
    : "";

  return (
    <div className="space-y-5">
      {cabecera}

      <Tarjeta titulo="¿A quiénes?" sub={`La campaña le llegó a ${n(datos.llegaron)} ${datos.llegaron === 1 ? "persona" : "personas"}.`}>
        <fieldset className="space-y-3">
          <legend className="sr-only">A quiénes de los que la recibieron</legend>
          {CRITERIOS_SEGUIMIENTO.map((k) => {
            const d = datos.criterios?.[k];
            const puede = !!d?.disponible;
            const activo = criterio === k;
            return (
              <div key={k} className={`rounded-xl border p-4 ${activo ? "border-violet-300 bg-violet-50/50" : "border-slate-200"} ${puede ? "" : "opacity-80"}`}>
                <label className={`flex items-start gap-3 ${puede ? "cursor-pointer" : "cursor-not-allowed"}`}>
                  <input type="radio" name="criterio" value={k} checked={activo} disabled={!puede} onChange={() => setCriterio(k)} className="mt-1" />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-3 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">{TITULO_CRITERIO[k]}</span>
                      {d?.disponible && <span className="text-xs text-slate-500">{n(d.quedan)} de {n(d.cumplen)} sin las bajas{respondieron ? "" : " ni los que respondieron"}</span>}
                    </span>
                    <span className="block text-xs text-slate-500 mt-0.5">{DETALLE_CRITERIO[k]}</span>
                    {d && !d.disponible && (
                      <span className="block text-xs text-amber-800 mt-1">{d.mensaje}{d.accion ? ` ${d.accion}` : ""}</span>
                    )}
                  </span>
                </label>
                {k === "no_compraron" && activo && (
                  <div className="mt-3 ml-7 space-y-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      En los
                      <input type="number" min={1} max={DIAS_MAX} inputMode="numeric" value={diasTexto} onChange={(ev) => setDiasTexto(ev.target.value)}
                        aria-label="Días desde que les llegó" aria-invalid={diasMal}
                        className={`w-20 h-8 rounded-lg border px-2 text-sm ${diasMal ? "border-red-300" : "border-slate-200"}`} />
                      días desde que les llegó
                    </label>
                    {diasMal && <p className="text-xs text-red-700" role="alert">Los días van de 1 a {DIAS_MAX}.</p>}
                    <label className="flex items-start gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={sinBase} onChange={(ev) => setSinBase(ev.target.checked)} className="mt-1" />
                      <span>
                        Sumar a los que no están en la base de ventas
                        <span className="block text-xs text-slate-500">
                          De ellos no se sabe si compraron{el && el.fueraDeLaBase ? ` (son ${n(el.fueraDeLaBase)})` : ""}.
                        </span>
                      </span>
                    </label>
                    {d?.disponible && d.advertencias.map((a) => <p key={a} className="text-xs text-amber-800">{a}</p>)}
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>
        <label className="mt-4 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={respondieron} onChange={(ev) => setRespondieron(ev.target.checked)} className="mt-1" />
          <span>
            Incluir a los que respondieron la campaña
            <span className="block text-xs text-slate-500">Por defecto quedan afuera: ya están hablando con la marca. Los que se dieron de baja no se incluyen nunca.</span>
          </span>
        </label>
      </Tarjeta>

      <Tarjeta>
        <div className="flex items-start gap-3" aria-live="polite">
          <Users size={18} className="mt-1 text-violet-600 flex-shrink-0" aria-hidden="true" />
          <div className="min-w-0 space-y-1">
            {error ? (
              <p className="text-sm text-red-700" role="alert">No pudimos contar cuántos quedan. {error}</p>
            ) : !el ? (
              <p className="text-sm text-slate-500">{cargando ? "Contando…" : "Elegí a quiénes para ver cuántos quedan."}</p>
            ) : (
              <>
                <p className="text-sm text-slate-800" data-testid="quedan-hoy">
                  Hoy quedan <strong className="text-2xl text-slate-900">{n(el.pueden)}</strong> {el.pueden === 1 ? "persona" : "personas"}
                  {cargando && <Loader2 size={13} className="inline ml-2 animate-spin text-slate-400" />}
                </p>
                <p className="text-xs text-slate-500">
                  De {n(el.cumplen)} que {el.cumplen === 1 ? "cumple" : "cumplen"} «{TITULO_CRITERIO[criterio as CriterioSeguimiento]}».
                  {desglose.length > 0 && <> Afuera: {desglose.join(" · ")}.</>}
                </p>
                <p className="text-xs text-slate-500">
                  Lo que mandó esta campaña no cuenta para la fatiga del seguimiento; las demás campañas sí. Es la cuenta de hoy: al confirmar el seguimiento se vuelve a calcular.
                </p>
              </>
            )}
          </div>
        </div>
      </Tarjeta>

      <div className="flex items-center gap-3 flex-wrap">
        <button type="button" onClick={() => void armar()} disabled={!!motivoBoton || armando}
          className="h-10 px-4 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 inline-flex items-center gap-1.5 disabled:opacity-50">
          {armando ? <Loader2 size={14} className="animate-spin" /> : <CornerDownRight size={14} />} {armando ? "Armando…" : "Armar el seguimiento"}
        </button>
        {motivoBoton && !armando && <span className="text-xs text-slate-500">{motivoBoton}</span>}
        {errorArmar && <span className="text-xs text-red-700" role="alert">{errorArmar}</span>}
      </div>
    </div>
  );
}
